import { useRuntimeVariables } from 'app-kit'
import { useGeckoAuth } from 'features/auth'
import type { ConfirmButtonVariant } from 'features/libra/components/confirmButtonVariant'
import { getStringTag } from 'features/libra/helpers/getStringTag'
import { stripComponents } from 'features/libra/ontology/ontologyFieldTypes'
import {
  resolveSignoffGate,
  type SignoffGate,
} from 'features/libra/signoff/signoffRequirements'
import { useTicketSignoffs } from 'features/libra/signoff/TicketSignoffsContext'
import { createLifecycleNotification } from 'features/libra/ticket/createLifecycleNotifications'
import { buildFieldValuesPatch } from 'features/libra/ticket/fieldValuesPatch'
import type {
  LibraTicket,
  LibraTicketGroup,
  UpdateTicketArgs,
} from 'features/libra/ticket/libraTicketsApi'
import { useTicketWriter } from 'features/libra/ticket/TicketWriterContext'
import { useWorkflow } from 'features/libra/WorkflowContext'
import { useGeckoPermissions } from 'features/permissions'
import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
} from 'react'
import { createLibraUrl } from 'routes/libra/-searchParams'
import { RolodexEntryType } from 'types'

import { appendJustification } from './lifecycleJustification'
import {
  findTransitionFromStatus,
  resolveActiveStageId,
} from './lifecycleSideEffects'
import { isReviewSubmission } from './lifecycleStageHelpers'
import type {
  LifecycleConfig,
  LifecycleConfirm,
  LifecycleJustify,
  LifecycleStage,
  LifecycleTransition,
} from './lifecycleTypes'
import {
  findMatchingTransition,
  getAvailableActions,
  getWorkflowCompleteLabel,
  LifecycleTriggerType,
  makeTicketGroupRoleCheck,
} from './lifecycleTypes'

// ---------------------------------------------------------------------------
// Available action — a transition the current user can trigger
// ---------------------------------------------------------------------------

export interface LifecycleActionExtra {
  /** Saved to the slots named by the action's `justify` config. */
  justification?: string
}

export interface LifecycleAction {
  transition: LifecycleTransition
  label: string
  component?: string
  style?: ConfirmButtonVariant
  confirm?: LifecycleConfirm
  justify?: LifecycleJustify
  /**
   * Set when a config gate holds this transition back. The action stays in the
   * list — unlike a missing role, which filters it out — so the button can be
   * rendered disabled with the reason.
   */
  blocked?: { reason: string }
  execute: (extra?: LifecycleActionExtra) => Promise<void>
}

export interface LifecycleActionGroup {
  componentKey: string
  actions: LifecycleAction[]
}

const DEFAULT_ACTION_COMPONENT = 'TicketStatusBanner'

export function groupActionsByComponent(
  actions: ReadonlyArray<LifecycleAction>
): LifecycleActionGroup[] {
  const map = new Map<string, LifecycleAction[]>()
  for (const action of actions) {
    const key = action.component ?? DEFAULT_ACTION_COMPONENT
    const group = map.get(key)
    if (group) {
      group.push(action)
    } else {
      map.set(key, [action])
    }
  }
  return Array.from(map.entries()).map(([componentKey, grouped]) => ({
    componentKey,
    actions: grouped,
  }))
}

// ---------------------------------------------------------------------------
// Context shape
// ---------------------------------------------------------------------------

export interface TicketLifecycleContextType {
  lifecycleConfig: LifecycleConfig | undefined
  currentStage: LifecycleStage | undefined
  availableActions: ReadonlyArray<LifecycleAction>
  actionGroups: ReadonlyArray<LifecycleActionGroup>
  /**
   * `makeTicketGroupRoleCheck` bound to this ticket, so step components gate on
   * config-authored roles exactly as action transitions do.
   */
  userHasRoles: (roles: ReadonlyArray<string>) => boolean
  /**
   * Undefined when the ticket carries no group tag, which fails every
   * non-empty `userHasRoles` check — exposed so callers can tell that apart
   * from the viewer lacking the role.
   */
  groupId: string | undefined
  isLocked: boolean
  isTerminal: boolean
  /** True when a `workflow_complete` transition exists from the current stage
   *  (or when no lifecycle config is present, i.e. legacy mode). */
  canCompleteWorkflow: boolean
  /** Fires `workflow_complete` auto-transition (if configured) as a side-effect
   *  of setting workflowComplete=true. Called by TicketAccessContext on last step. */
  completeWorkflow: () => Promise<void>
  /** Label for the last-step "Complete" button, derived from config. */
  workflowCompleteLabel: string
}

const TicketLifecycleContext = createContext<TicketLifecycleContextType | null>(
  null
)

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export const TicketLifecycleProvider: FC<{
  children: ReactNode
  ticket: LibraTicket
  ticketGroup: LibraTicketGroup | undefined
}> = ({ children, ticket, ticketGroup }) => {
  const workflow = useWorkflow()
  const { user, hasPermissions } = useGeckoPermissions()
  const { ALERTS_URL, BASE_PATH } = useRuntimeVariables()
  const { authorizedAxios } = useGeckoAuth()
  const { enqueue } = useTicketWriter()
  const {
    signoffs,
    isLoading: signoffsLoading,
    deactivateAll,
  } = useTicketSignoffs()

  const lifecycleConfig = workflow.lifecycleConfig
  const signoffConfig = workflow.signoffConfig
  const groupId = getStringTag(ticket.tags, RolodexEntryType.LIBRA_TICKET_GROUP)

  // Sign-off `requiredWhen` conditions are ticket-scope, so the per-component
  // subtree never takes part.
  const ticketFieldValues = useMemo(
    () => stripComponents(ticket.fieldValues),
    [ticket.fieldValues]
  )

  const userHasRoles = useMemo(
    () => makeTicketGroupRoleCheck(hasPermissions, groupId),
    [hasPermissions, groupId]
  )

  const currentStage = useMemo<LifecycleStage | undefined>(() => {
    if (!lifecycleConfig) return undefined
    const stageId = resolveActiveStageId(lifecycleConfig, ticket.ticketStatus)
    return lifecycleConfig.stages.find((s) => s.id === stageId)
  }, [lifecycleConfig, ticket.ticketStatus])

  const ticketUrl = useMemo(
    () =>
      `${window.location.origin}${BASE_PATH}${createLibraUrl({
        orgId: ticket.tags.organization ?? undefined,
        workflowId: ticket.workflowId,
        ticket: ticket.id,
        thread: undefined,
      })}`,
    [BASE_PATH, ticket.tags.organization, ticket.workflowId, ticket.id]
  )

  const recipientUserIds = useMemo(() => {
    const owners = ticketGroup ? ticketGroup.owners : []
    return [...new Set([...owners, ...ticket.contributors])]
  }, [ticketGroup, ticket.contributors])

  const sendNotification = useCallback(
    (stageName: string) => {
      if (!user) return
      void createLifecycleNotification({
        ticketTitle: ticket.title,
        organizationId: ticket.tags.organization ?? '',
        recipientUserIds,
        actorId: user.id,
        actorName: user.name ?? user.email,
        alertsUrl: ALERTS_URL,
        axios: authorizedAxios,
        ticketUrl,
        stageName,
      })
    },
    [
      user,
      ticket.title,
      ticket.tags.organization,
      recipientUserIds,
      ALERTS_URL,
      authorizedAxios,
      ticketUrl,
    ]
  )

  const availableActions = useMemo<LifecycleAction[]>(() => {
    if (!lifecycleConfig || !currentStage) return []
    const actionTransitions = getAvailableActions(
      lifecycleConfig,
      currentStage.id,
      userHasRoles
    )
    return actionTransitions.map((actionTransition) => {
      const trigger = actionTransition.trigger as Extract<
        typeof actionTransition.trigger,
        { type: LifecycleTriggerType.ACTION }
      >
      const targetStage = lifecycleConfig.stages.find(
        (s) => s.id === actionTransition.to
      )
      const gate: SignoffGate | undefined = !trigger.requireSignoffs
        ? undefined
        : signoffsLoading
          ? { satisfied: false, reason: 'Loading sign-off status' }
          : resolveSignoffGate(signoffConfig, signoffs, ticketFieldValues)
      return {
        transition: actionTransition,
        label: trigger.label,
        component: trigger.component,
        style: trigger.style,
        confirm: trigger.confirm,
        justify: trigger.justify,
        blocked:
          gate?.satisfied === false ? { reason: gate.reason } : undefined,
        execute: async (extra) => {
          // Reset before the stage change: if the stage change then fails,
          // reviewers just re-sign; the other order could reopen a ticket with
          // its signatures still counting. A failed reset throws, so the
          // transition never runs.
          if (trigger.shouldDeactivateSignoffs) {
            await deactivateAll()
          }

          const applied = await enqueue(
            (latest) => {
              // `requiredWhen` is re-checked against the writer's snapshot, not
              // the render-time ticket: eligibility may have changed since this
              // button was drawn.
              if (
                trigger.requireSignoffs &&
                !resolveSignoffGate(
                  signoffConfig,
                  signoffs,
                  stripComponents(latest.fieldValues)
                ).satisfied
              ) {
                return null
              }

              const updateArgs: Omit<UpdateTicketArgs, 'ticketId'> = {
                ticketStatus:
                  actionTransition.to as UpdateTicketArgs['ticketStatus'],
              }
              if (trigger.justify && extra?.justification) {
                updateArgs.fieldValues = buildFieldValuesPatch(
                  latest.fieldValues,
                  { kind: 'ticket' },
                  appendJustification(latest.fieldValues, trigger.justify.id, {
                    text: extra.justification,
                    justifiedById: user?.id ?? '',
                    justifiedBy: user?.name ?? user?.email ?? '',
                    justifiedAt: new Date().toISOString(),
                    action: trigger.label,
                    fromStage: actionTransition.from,
                    toStage: actionTransition.to,
                  })
                )
              }
              if (targetStage?.terminal && user) {
                updateArgs.terminalTransitionBy = user.name ?? user.email
                updateArgs.terminalTransitionAt = new Date().toISOString()
              }
              if (isReviewSubmission(actionTransition) && user) {
                updateArgs.submittedForReviewBy = user.name ?? user.email
                updateArgs.submittedForReviewAt = new Date().toISOString()
              }
              if (!targetStage?.terminal) {
                updateArgs.workflowComplete = false
              }
              return updateArgs
            },
            // Lifecycle action transitions ARE the lifecycle — don't fire
            // FIRST_INTERACTION side effects again.
            { applyInteractionSideEffects: false }
          )
          if (applied && targetStage) {
            sendNotification(targetStage.name)
          }
        },
      }
    })
  }, [
    lifecycleConfig,
    currentStage,
    userHasRoles,
    enqueue,
    sendNotification,
    user,
    signoffConfig,
    signoffs,
    signoffsLoading,
    deactivateAll,
    ticketFieldValues,
  ])

  const canCompleteWorkflow = useMemo(() => {
    if (!lifecycleConfig) return true
    if (!currentStage) return false
    return (
      findMatchingTransition(lifecycleConfig, currentStage.id, {
        type: LifecycleTriggerType.WORKFLOW_COMPLETE,
      }) !== null
    )
  }, [lifecycleConfig, currentStage])

  const completeWorkflow = useCallback(async () => {
    if (!lifecycleConfig) {
      await enqueue(() => ({ workflowComplete: true }), {
        applyInteractionSideEffects: false,
      })
      return
    }

    // Resolve the active stage + matching transition INSIDE the enqueue
    // closure against the writer's `latest` snapshot — NOT the render-time
    // `currentStage`. A queued field-save that advanced ticketStatus
    // mid-flow would otherwise leave the WORKFLOW_COMPLETE lookup keyed
    // off the wrong stage. The applied patch flows back out through the
    // enqueue promise (no closure side channel).
    const applied = await enqueue(
      (latest) => {
        const transition = findTransitionFromStatus(
          lifecycleConfig,
          latest.ticketStatus,
          { type: LifecycleTriggerType.WORKFLOW_COMPLETE }
        )
        if (!transition) return null
        const targetStage = lifecycleConfig.stages.find(
          (s) => s.id === transition.to
        )
        const updateArgs: Omit<UpdateTicketArgs, 'ticketId'> = {
          workflowComplete: true,
          ticketStatus: transition.to as UpdateTicketArgs['ticketStatus'],
        }
        if (targetStage?.terminal && user) {
          updateArgs.terminalTransitionBy = user.name ?? user.email
          updateArgs.terminalTransitionAt = new Date().toISOString()
        }
        if (isReviewSubmission(transition) && user) {
          updateArgs.submittedForReviewBy = user.name ?? user.email
          updateArgs.submittedForReviewAt = new Date().toISOString()
        }
        return updateArgs
      },
      { applyInteractionSideEffects: false }
    )
    // Side effects were disabled above, so `applied.ticketStatus` is
    // exactly the transition target the write landed with.
    const appliedStatus = applied?.ticketStatus
    if (appliedStatus) {
      const targetStage = lifecycleConfig.stages.find(
        (s) => s.id === appliedStatus
      )
      if (targetStage) {
        sendNotification(targetStage.name)
      }
    }
  }, [lifecycleConfig, enqueue, user, sendNotification])

  const workflowCompleteLabel = useMemo(
    () => getWorkflowCompleteLabel(lifecycleConfig, currentStage?.id ?? ''),
    [lifecycleConfig, currentStage?.id]
  )

  const isLocked = currentStage?.locked ?? false
  const isTerminal = currentStage?.terminal ?? false

  const actionGroups = useMemo(
    () => groupActionsByComponent(availableActions),
    [availableActions]
  )

  const value = useMemo<TicketLifecycleContextType>(
    () => ({
      lifecycleConfig,
      currentStage,
      availableActions,
      actionGroups,
      userHasRoles,
      groupId,
      isLocked,
      isTerminal,
      canCompleteWorkflow,
      completeWorkflow,
      workflowCompleteLabel,
    }),
    [
      lifecycleConfig,
      currentStage,
      availableActions,
      actionGroups,
      userHasRoles,
      groupId,
      isLocked,
      isTerminal,
      canCompleteWorkflow,
      completeWorkflow,
      workflowCompleteLabel,
    ]
  )

  return (
    <TicketLifecycleContext.Provider value={value}>
      {children}
    </TicketLifecycleContext.Provider>
  )
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export const useTicketLifecycle = (): TicketLifecycleContextType => {
  const ctx = useContext(TicketLifecycleContext)
  if (!ctx) {
    throw new Error(
      'useTicketLifecycle must be used within a TicketLifecycleProvider'
    )
  }
  return ctx
}
